import React from "react";
import { useWatch, useFormContext } from "react-hook-form";
import {
  attributeNameLabelMap,
  attributeTypeLabelMap,
  criteriaLabelMap,
} from "../../../../components/SegmentSummary/SegmentSummary.helpers";
import { ConditionProps } from "./Condition";
import {
  compareOptions,
  getConditionValueComponent,
  getConditionValueType,
  getEmptyValue,
} from "./Condition.helpers";
import { getDependentConditionError } from "../ConditionGroup.helpers";

export interface AttributeTypeOption {
  label: string;
  value: string;
  /** Set when the option is listed but cannot be chosen on this row. */
  disabledReason?: string;
}

export function useCondition(props: ConditionProps) {
  const {
    showDivider,
    index,
    segmentConditionsInfo,
    groupIndex,
    onDelete,
    prefixName: prefixNameProp,
    attributesTypes,
    disabledAttributeTypes,
    disabled,
  } = props;

  const { setValue, unregister, trigger, getValues } = useFormContext();

  const prefixName = `${prefixNameProp}.conditions.${index}`;

  const [criteriaChange, setCriteriaChange] = React.useState(false);

  const valueKey = React.useMemo(
    () => `${prefixName}${criteriaChange}`,
    [criteriaChange, prefixName]
  );

  const attributeType = useWatch({
    name: `${prefixName}.attributeType`,
  });

  const attributeName = useWatch({
    name: `${prefixName}.attributeName`,
  });

  const criteria = useWatch({
    name: `${prefixName}.criteria`,
  });

  const deleteCondition = React.useCallback(() => {
    onDelete(index);
  }, [index, onDelete]);

  const changeAttributeType = React.useCallback(
    (value: string) => {
      setValue(prefixName, {
        type: "base",
        attributeType: value,
        attributeName: "",
        criteria: "",
        value: {},
      });
      trigger();
    },
    [prefixName, setValue, trigger]
  );

  const changeAttributeName = React.useCallback(
    (value: string) => {
      setValue(prefixName, {
        type: "base",
        attributeType,
        attributeName: value,
        criteria: "",
        value: {},
      });
      trigger();
    },
    [attributeType, prefixName, setValue, trigger]
  );

  const attributeTypeOptions = React.useMemo(() => {
    const options: AttributeTypeOption[] = [];
    for (const key of attributesTypes) {
      options.push({
        value: key,
        label: attributeTypeLabelMap[key] || key,
        disabledReason: disabledAttributeTypes?.[key],
      });
    }
    return options.sort(compareOptions);
  }, [attributesTypes, disabledAttributeTypes]);

  // SENDER / LINK_URL rows stay in the form when the Activity they depend on
  // goes away, but must block Save / Refresh Summary until it is restored.
  const attributeTypeRules = React.useMemo(
    () => ({
      required: {
        value: true,
        message: "Required field",
      },
      validate: {
        dependentActivity: (value: string) =>
          getDependentConditionError(
            value,
            getValues(`${prefixNameProp}.conditions`)
          ) ?? true,
      },
    }),
    [getValues, prefixNameProp]
  );

  const attributeNameOptions = React.useMemo(() => {
    const options: { label: string; value: string }[] = [];
    if (!attributeType) {
      return options;
    }
    const type = segmentConditionsInfo?.attributesTypes?.[attributeType];
    type?.attributes?.forEach((attr: any) => {
      options.push({
        value: attr.name,
        label: attributeNameLabelMap[attr.name] || attr.name,
      });
    });
    return options.sort(compareOptions);
  }, [attributeType, segmentConditionsInfo?.attributesTypes]);

  const criteriaOptions = React.useMemo(() => {
    const options: { label: string; value: string }[] = [];
    if (!attributeType) {
      return options;
    }
    const type = segmentConditionsInfo?.attributesTypes?.[attributeType];
    const attr = type?.attributes.find(
      (att: any) => att.name === attributeName
    );
    for (const key in attr?.criteria) {
      options.push({
        value: key,
        label: criteriaLabelMap[key] || key,
      });
    }
    return options.sort(compareOptions);
  }, [attributeName, attributeType, segmentConditionsInfo?.attributesTypes]);

  const ComponentValue = React.useMemo(() => {
    return getConditionValueComponent(
      segmentConditionsInfo,
      attributeType,
      attributeName,
      criteria
    );
  }, [attributeName, attributeType, criteria, segmentConditionsInfo]);

  const valueType = React.useMemo(() => {
    return getConditionValueType(
      segmentConditionsInfo,
      attributeType,
      attributeName,
      criteria
    );
  }, [attributeName, attributeType, criteria, segmentConditionsInfo]);

  const changeCriteria = React.useCallback(
    (value: string) => {
      const emptyValue = getEmptyValue(
        segmentConditionsInfo,
        attributeType,
        attributeName,
        value
      );
      const nameToUnregister = valueType?.endsWith("[]")
        ? [`${prefixName}.value.value[0]`, `${prefixName}.value.value[1]`]
        : [`${prefixName}.value.value`];

      unregister(nameToUnregister);
      setValue(prefixName, {
        type: "base",
        attributeType,
        attributeName,
        criteria: value,
        value: emptyValue,
      });
      trigger();
      setCriteriaChange((old) => !old);
    },
    [
      attributeName,
      attributeType,
      prefixName,
      segmentConditionsInfo,
      setValue,
      trigger,
      unregister,
      valueType,
    ]
  );

  React.useLayoutEffect(() => {
    if (attributeTypeOptions.length === 1) {
      const value = attributeTypeOptions[0].value;
      setValue(`${prefixName}.attributeType`, value);
    }
  }, [
    attributeNameOptions,
    attributeNameOptions.length,
    attributeTypeOptions,
    groupIndex,
    index,
    prefixName,
    setValue,
  ]);

  React.useLayoutEffect(() => {
    if (attributeNameOptions.length === 1) {
      const value = attributeNameOptions[0].value;
      setValue(`${prefixName}.attributeName`, value);
    }
  }, [
    attributeNameOptions,
    attributeNameOptions.length,
    groupIndex,
    index,
    prefixName,
    setValue,
  ]);

  const shouldShowAttrType = attributeTypeOptions.length > 1;
  const shouldShowAttrName = attributeType && attributeNameOptions.length > 1;
  const shouldShowCriteria = attributeName || attributeNameOptions.length === 1;
  const shouldShowValue = attributeName || attributeNameOptions.length === 1;

  return {
    ComponentValue,
    attributeNameOptions,
    attributeTypeOptions,
    attributeTypeRules,
    changeAttributeName,
    changeAttributeType,
    changeCriteria,
    criteriaOptions,
    deleteCondition,
    onDelete,
    prefixName,
    shouldShowAttrType,
    shouldShowAttrName,
    shouldShowCriteria,
    shouldShowValue,
    showDivider,
    valueType,
    disabled,
    valueKey,
  };
}
